import { NumberFlow } from "@patient-watch/forms";
import { ui } from "@patient-watch/ui";
import { resolveStep, stepDecimals } from "../../features/inputs/inputStep";

interface Props {
    /** The question's input id, for the number box's test id (form-number-<inputId>). */
    inputId?: string;
    label?: string;
    maxLength?: number;
    description?: string;
    required?: boolean;
    value: string;
    setValue: (text: string) => void;
    unit?: string | null;
    decimalPoints?: number | null;
    /** inputs.step. Wins over decimalPoints when set. */
    step?: number | null;
    min?: number | null;
    max?: number | null;
    /** Shown inline when the typed value is outside min and max. See NumberFlow. */
    validationMessage?: string | null;
}

const NumberInput = (props: Props) => {
    const decimalStep = props.decimalPoints ? 1 / 10 ** props.decimalPoints : 1;
    const step = resolveStep(props.step, decimalStep);
    const fractionDigits = Math.max(props.decimalPoints ?? 0, stepDecimals(step));
    const parsed = props.value.trim() === "" ? null : Number(props.value);
    const value = parsed != null && Number.isFinite(parsed) ? parsed : null;

    const rangeHint =
        props.min != null && props.max != null
            ? `Enter a number between ${props.min} and ${props.max}.`
            : props.min != null
              ? `Enter a number of at least ${props.min}.`
              : props.max != null
                ? `Enter a number up to ${props.max}.`
                : null;

    return (
        <>
            {props.label && (
                <ui.Label>
                    {props.label}
                    {props.required && <span className="ml-0.5 text-red-500">*</span>}
                </ui.Label>
            )}
            <div className="flex items-center gap-2">
                <NumberFlow
                    className="flex-1 min-w-0"
                    aria-label={props.label ?? "Number"}
                    value={value}
                    onChange={(next) => props.setValue(next == null ? "" : String(next))}
                    min={props.min ?? undefined}
                    max={props.max ?? undefined}
                    step={step}
                    fractionDigits={fractionDigits}
                    validationMessage={props.validationMessage}
                    allowEmpty
                    data-testid={props.inputId ? `form-number-${props.inputId}` : undefined}
                />
                {props.unit ? <span className="font-medium text-slate-500 text-lg shrink-0">{props.unit}</span> : null}
            </div>
            <p className="font-normal text-slate-700 text-sm">{props.description ?? rangeHint ?? "Type a number."}</p>
        </>
    );
};

export default NumberInput;
